'use strict';

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { browserHistory } from 'react-router';

import UserStore from '@stores/UserStore';
import { fetchProviderConnected } from '@utils/ProviderConnection';
import { PROVIDERS } from '@tables/user-providers';

import './FitbitDeprecationBanner.scss';

export default class FitbitDeprecationBanner extends Component {
    static propTypes = {
        onMigrateClick: PropTypes.func,
    };

    constructor(props) {
        super(props);

        this.state = {
            hasFitbit: false,
            dismissed: false,
        };

        this.bannerResolved = false;
        this._abortController = new AbortController();
    }

    componentDidMount() {
        UserStore.addChangeListener(this.onUserStoreChange);
        this.resolveBannerState();
    }

    componentWillUnmount() {
        UserStore.removeChangeListener(this.onUserStoreChange);
        // Cancels the in-flight provider fetch so its .then never fires
        // setState on an unmounted component.
        this._abortController.abort();
    }

    // Flux stores hydrate asynchronously; if the user isn't available yet
    // at mount, retry once the store emits so the deprecation notice is
    // not permanently suppressed for a genuinely connected user.
    onUserStoreChange = () => {
        this.resolveBannerState();
    };

    resolveBannerState = () => {
        if (this.bannerResolved) {
            return;
        }

        const userUuid = this.getUserUuid();
        if (!userUuid) {
            return;
        }

        this.bannerResolved = true;

        const storageKey = this.storageKeyFor(userUuid);
        if (storageKey && localStorage.getItem(storageKey) === '1') {
            this.setState({ dismissed: true });
            return;
        }

        this.fetchFitbitConnection();
    }

    getUserUuid = () => {
        const user = UserStore.getUser ? UserStore.getUser() : null;
        return (user && user.uuid) || null;
    };

    storageKeyFor = (uuid) => (uuid ? `fitbit-deprecation-dismissed-${uuid}` : null);

    fetchFitbitConnection = () => {
        fetchProviderConnected(PROVIDERS.FITBIT, this._abortController).then((connected) => {
            this.setState({ hasFitbit: connected });
        });
    };

    handleDismiss = () => {
        const storageKey = this.storageKeyFor(this.getUserUuid());
        if (storageKey) {
            localStorage.setItem(storageKey, '1');
        }
        this.setState({ dismissed: true });
    };

    handleMigrate = () => {
        if (this.props.onMigrateClick) {
            this.props.onMigrateClick();
            return;
        }
        browserHistory.push('/apps-and-devices');
    };

    render() {
        const { hasFitbit, dismissed } = this.state;
        if (!hasFitbit || dismissed) {
            return null;
        }

        return (
            <div
                className="fitbit-deprecation-banner"
                role="alert"
                data-testid="fitbit-deprecation-banner">
                <div className="fitbit-deprecation-banner-text">
                    <strong>The Fitbit app is now Google Health.</strong>{' '}
                    Reconnect to keep your activity syncing. Your Fitbit and all your history
                    come with it.
                </div>
                <div className="fitbit-deprecation-banner-actions">
                    <button
                        type="button"
                        className="fitbit-deprecation-banner-cta"
                        onClick={this.handleMigrate}>
                        Reconnect
                    </button>
                    <button
                        type="button"
                        className="fitbit-deprecation-banner-dismiss"
                        aria-label="Dismiss banner"
                        onClick={this.handleDismiss}>
                        ×
                    </button>
                </div>
            </div>
        );
    }
}
